import React from 'react'
import { X, Edit, Star, Calendar, Package, Hash, Tag } from 'lucide-react'
import { Modal, Button } from '@components/common'
import './FeatureView.css'

/**
 * FeatureView Component
 *
 * Read-only view of feature details with organized sections
 *
 * Features:
 * - Comprehensive feature information display
 * - Formatted values (dates)
 * - Status badges
 * - Quick edit action
 * - Accessible and responsive
 *
 * @param {Object} featureData - Feature data to display
 * @param {Function} onClose - Close modal callback
 * @param {Function} onEdit - Edit action callback (optional)
 */
const FeatureView = ({ featureData, onClose, onEdit }) => {
	if (!featureData) return null

	/**
	 * Format date
	 */
	const formatDate = (dateString) => {
		if (!dateString) return '-'
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		})
	}

	/**
	 * Get status badge class
	 */
	const getStatusClass = (status) => {
		return status === 'active' ? 'feature-view__badge--success' : 'feature-view__badge--secondary'
	}

	const footer = (
		<>
			{onEdit && !featureData.deleted_at && featureData.status !== 'archived' && (
				<Button variant="primary" onClick={onEdit}>
					<Edit size={18} />
					<span>Edit Feature</span>
				</Button>
			)}
			<Button variant="secondary" onClick={onClose}>
				Close
			</Button>
		</>
	)

	return (
		<Modal
			isOpen={true}
			onClose={onClose}
			title="Feature Details"
			size="large"
			footer={footer}
			closeOnBackdrop={false}
			closeOnEscape={false}
		>
			<div className="feature-view">
				{/* Header Section */}
				<div className="feature-view__header">
					<div className="feature-view__header-content">
						<div className="feature-view__icon">
							<Star size={32} />
						</div>
						<div>
							<h2 className="feature-view__title">{featureData.name}</h2>
							<p className="feature-view__slug">{featureData.slug}</p>
						</div>
					</div>
					<div className="feature-view__header-badges">
						<div className={`feature-view__badge ${getStatusClass(featureData.status)}`}>
							{featureData.status}
						</div>
						{featureData.category && (
							<div className="feature-view__badge feature-view__badge--category">
								{featureData.category}
							</div>
						)}
					</div>
				</div>

				{/* Description */}
				{featureData.description && (
					<div className="feature-view__section">
						<p className="feature-view__description">{featureData.description}</p>
					</div>
				)}

				{/* Feature Information */}
				<div className="feature-view__section">
					<h3 className="feature-view__section-title">Feature Information</h3>
					<div className="feature-view__grid">
						{featureData.category && (
							<div className="feature-view__item">
								<div className="feature-view__item-icon">
									<Tag size={20} />
								</div>
								<div className="feature-view__item-content">
									<span className="feature-view__item-label">Category</span>
									<span className="feature-view__item-value">{featureData.category}</span>
								</div>
							</div>
						)}

						<div className="feature-view__item">
							<div className="feature-view__item-icon">
								<Hash size={20} />
							</div>
							<div className="feature-view__item-content">
								<span className="feature-view__item-label">Display Order</span>
								<span className="feature-view__item-value">{featureData.display_order || 0}</span>
							</div>
						</div>

						<div className="feature-view__item">
							<div className="feature-view__item-icon">
								<Package size={20} />
							</div>
							<div className="feature-view__item-content">
								<span className="feature-view__item-label">Used in Packages</span>
								<span className="feature-view__item-value">
									{featureData.usage_stats?.package_count || 0} package(s)
								</span>
							</div>
						</div>
					</div>
				</div>

				{/* Metadata */}
				<div className="feature-view__section">
					<h3 className="feature-view__section-title">Metadata</h3>
					<div className="feature-view__metadata">
						<div className="feature-view__metadata-item">
							<span className="feature-view__metadata-label">Created</span>
							<span className="feature-view__metadata-value">
								{formatDate(featureData.created_at)}
							</span>
						</div>
						<div className="feature-view__metadata-item">
							<span className="feature-view__metadata-label">Last Updated</span>
							<span className="feature-view__metadata-value">
								{formatDate(featureData.updated_at)}
							</span>
						</div>
						{featureData.deleted_at && (
							<div className="feature-view__metadata-item">
								<span className="feature-view__metadata-label">Archived</span>
								<span className="feature-view__metadata-value">
									{formatDate(featureData.deleted_at)}
								</span>
							</div>
						)}
					</div>
				</div>
			</div>
		</Modal>
	)
}

export default FeatureView
